/**
 * SessionGate — the only gate between an authenticated user and the L4E
 * application shell.
 *
 * Replaces the prior TwoFactorGate, which only blocked when a 2FA challenge
 * was actively in flight. That gate left the entire app shell visible
 * during silent refresh, after a silent refresh failure, or to any user
 * who reloaded the browser before completing TOTP. Audit F1.
 *
 * Behavior (aligned with DEV-725 progression spec):
 *
 *   - isLoading (silent refresh in flight on a private path):
 *       Render <AuthSplash />. No chrome, no LeftNav, no topbar.
 *
 *   - requires2FA (TOTP challenge active for this session):
 *       Render <TotpVerifyScreen />. Takes precedence even on public
 *       paths — a user who reloads mid-2FA stays in the challenge.
 *
 *   - !isAuthenticated on a private path:
 *       Navigate to /login, preserving the original location in
 *       state.from so LoginPage can return the user there after
 *       successful authentication.
 *
 *   - Public path (/welcome, /login, /register, /reset-password):
 *       Render children. Those routes resolve to LoginPage,
 *       RegisterPage, etc. without an auth requirement.
 *
 *   - isFakeAuth (dev-only window.__L4E_FAKE_AUTH flag):
 *       Render children. Dev convenience; FakeAuthMiddleware is off
 *       in non-dev environments (UseFakeAuth=false in appsettings).
 *
 * Security principle: until a user has a valid JWT AND, for users with
 * TotpEnabled=true, has completed the TOTP challenge in this session,
 * NO part of the L4E application may render. The splash and TOTP screen
 * are intentionally chrome-free.
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import AuthSplash from './AuthSplash';
import TotpVerifyScreen from '../Components/TotpSetup/TotpVerifyScreen';
import SmallDeviceNotice from './SmallDeviceNotice';
import { useSmallDeviceBlock } from './useSmallDeviceBlock';
import PublicShell from './PublicShell';

/**
 * Routes that render without authentication. Must be kept aligned with the
 * public Routes block in App.tsx's RouteGate. Listed in one place so an
 * accidental drift between the two checks is grep-able.
 */
// '/invite' covers the tokenized invitation-accept landing (/invite/:token),
// which must be reachable pre-auth (DEV-1307 / onboarding Slice C).
const PUBLIC_PATHS = ['/welcome', '/login', '/register', '/reset-password', '/invite'];

function isPublicPath(pathname: string): boolean {
  return PUBLIC_PATHS.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`),
  );
}

interface Props {
  children: React.ReactNode;
}

export default function SessionGate({ children }: Props) {
  const { isLoading, isAuthenticated, requires2FA, isFakeAuth } = useAuth();
  const location = useLocation();
  // Only probe the small-device setting once authenticated — public pages are
  // never blocked, and probing pre-login produced a stray 404.
  const smallDeviceBlocked = useSmallDeviceBlock(isAuthenticated);

  // Dev convenience. Production builds never set window.__L4E_FAKE_AUTH;
  // FakeAuthMiddleware is also gated by UseFakeAuth=false on the backend.
  if (isFakeAuth) return <>{children}</>;

  // 2FA challenge takes precedence over everything — including public
  // paths. A user who is mid-challenge stays in the challenge across
  // browser refreshes (silentRefresh re-fetches the partial token from
  // the backend on mount; see AuthContext silentRefresh + DEV-946).
  if (requires2FA) return <TotpVerifyScreen />;

  const publicPath = isPublicPath(location.pathname);

  // Splash during the initial silent refresh on private paths. Public
  // paths render their own content immediately (the login card has its
  // own loading states).
  if (isLoading && !publicPath) return <AuthSplash />;

  // Public paths: an UNAUTHENTICATED visitor gets only the lightweight public
  // shell (landing / login / register). Critically, this does NOT mount <App/>,
  // so none of the authenticated data hooks or SignalR hubs fire before login
  // (no 401 storm on the welcome page). An already-authenticated user on a
  // public path keeps the prior behavior (RouteGate handles it).
  if (publicPath) {
    return isAuthenticated ? <>{children}</> : <PublicShell />;
  }

  // Private path, not authenticated → bounce to the public landing page,
  // preserving the attempted URL in state.from so the post-auth flow can
  // navigate the user back after they sign up / log in.
  if (!isAuthenticated) {
    return <Navigate to="/welcome" state={{ from: location }} replace />;
  }

  // Admin-toggled gate (ui.block_small_devices): block the logged-in app on
  // phone-sized screens. Public paths already returned above, so the landing
  // and login pages remain usable on any device.
  if (smallDeviceBlocked) return <SmallDeviceNotice />;

  // Authenticated, 2FA satisfied, not loading → render the app.
  return <>{children}</>;
}
